import React, { useState, useEffect, useCallback } from "react";
import { showToast } from '../../utils/helpers';
import api from "../../utils/api"
import { API_CONFIG } from '../../utils/apiConfig';

const AddItemModal = ({ show, onHide, onAddItem, currency = 'NGN' }) => {
  const [activeTab, setActiveTab] = useState('existing');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [existingProducts, setExistingProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    description: "",
    quantity: 1,
    price: 0,
    amount: 0,
    discount: 0,
    tax_rate: 0 // Added tax_rate to match backend
  });

  // Memoized product loading with error handling
  const loadProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.post(API_CONFIG.PRODUCTS.MY_PRODUCTS);
      
      if (response.data && Array.isArray(response.data.products)) {
        setExistingProducts(response.data.products);
      } else {
        throw new Error('Invalid products data format');
      }
    } catch (error) {
      console.error('Failed to load products:', error);
      showToast('Failed to load products. Please try again.', 'error');
      setExistingProducts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      loadProducts();
    }
  }, [show, loadProducts]);

  // Optimized product filtering with useMemo
  const filteredProducts = React.useMemo(() => {
    if (!searchTerm.trim()) return existingProducts;
    
    const searchLower = searchTerm.toLowerCase();
    return existingProducts.filter(product =>
      (product.product_name?.toLowerCase() || '').includes(searchLower) ||
      (product.description?.toLowerCase() || '').includes(searchLower) ||
      (product.category?.toLowerCase() || '').includes(searchLower)
    );
  }, [existingProducts, searchTerm]);

  // Auto-calculate amount with validation
  useEffect(() => {
    const quantity = Math.max(0, formData.quantity || 0);
    const price = Math.max(0, formData.price || 0);
    const discount = Math.max(0, Math.min(formData.discount || 0, price));
    const discountedPrice = Math.max(0, price - discount);
    
    setFormData(prev => ({
      ...prev,
      amount: quantity * discountedPrice,
    }));
  }, [formData.quantity, formData.price, formData.discount]);

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      setFormData({
        description: "",
        quantity: 1,
        price: 0,
        amount: 0,
        discount: 0,
        tax_rate: 0
      });
      setSelectedProducts([]);
      setSearchTerm('');
      setActiveTab('existing');
      setIsSubmitting(false);
    }
  }, [show]);

  // Product selection handler
  const handleProductSelect = useCallback((product) => {
    setSelectedProducts(prev => {
      const existingIndex = prev.findIndex(p => p.id === product.id);
      
      if (existingIndex >= 0) {
        return prev.filter(p => p.id !== product.id);
      } else {
        const perUnitCost = parseFloat(product.per_unit_cost) || 0;
        return [...prev, { 
          ...product, 
          selectedQuantity: 1, 
          selectedDiscount: 0,
          selectedTaxRate: 0,
          selectedAmount: perUnitCost,
          price: perUnitCost // Add price field for consistency
        }];
      }
    });
  }, []);

  // Update selected product quantity
  const updateSelectedProductQuantity = useCallback((productId, quantity) => {
    setSelectedProducts(prev =>
      prev.map(product => {
        if (product.id === productId) {
          const newQuantity = Math.max(1, quantity);
          const price = parseFloat(product.per_unit_cost) || 0;
          const discount = product.selectedDiscount || 0;
          const discountedPrice = Math.max(0, price - discount);
          return { 
            ...product, 
            selectedQuantity: newQuantity,
            selectedAmount: newQuantity * discountedPrice
          };
        }
        return product;
      })
    );
  }, []);

  // Update selected product discount
  const updateSelectedProductDiscount = useCallback((productId, discount) => {
    setSelectedProducts(prev =>
      prev.map(product => {
        if (product.id === productId) {
          const price = parseFloat(product.per_unit_cost) || 0;
          const newDiscount = Math.max(0, Math.min(discount, price));
          const discountedPrice = Math.max(0, price - newDiscount);
          return { 
            ...product, 
            selectedDiscount: newDiscount,
            selectedAmount: product.selectedQuantity * discountedPrice
          };
        }
        return product;
      })
    );
  }, []);

  // Check if product is selected
  const isProductSelected = useCallback((productId) => {
    return selectedProducts.some(p => p.id === productId);
  }, [selectedProducts]);

  // Validate selected products
  const validateSelectedProducts = useCallback(() => {
    if (selectedProducts.length === 0) {
      showToast('Please select at least one product', 'error');
      return false;
    }

    for (const product of selectedProducts) {
      if (product.selectedQuantity <= 0) {
        showToast(`Quantity for ${product.product_name} must be greater than 0`, 'error');
        return false;
      }
      if (product.selectedDiscount < 0) {
        showToast(`Discount for ${product.product_name} cannot be negative`, 'error');
        return false;
      }
      
      const price = parseFloat(product.per_unit_cost) || 0;
      if (product.selectedDiscount > price) {
        showToast(`Discount for ${product.product_name} cannot exceed price`, 'error');
        return false;
      }
    }

    return true;
  }, [selectedProducts]);

  // Add selected products to invoice
  const handleAddSelectedProducts = async () => {
    if (!validateSelectedProducts()) return;

    setIsSubmitting(true);
    
    try {
      // Add all selected products with proper data structure for backend
      selectedProducts.forEach(product => {
        const itemData = {
          description: product.product_name || 'Unnamed Product',
          quantity: product.selectedQuantity,
          price: parseFloat(product.per_unit_cost) || 0,
          discount: product.selectedDiscount,
          tax_rate: product.selectedTaxRate || 0,
          amount: product.selectedAmount
        };
        
        onAddItem(itemData);
      });

      showToast(`${selectedProducts.length} item(s) added successfully`, 'success');
      onHide();
    } catch (error) {
      console.error('Error adding products:', error);
      showToast('Failed to add items. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Validate new item form
  const validateNewItem = useCallback(() => {
    if (!formData.description.trim()) {
      showToast('Please enter a description', 'error');
      return false;
    }

    if (formData.quantity <= 0) {
      showToast('Quantity must be greater than 0', 'error');
      return false;
    }

    if (formData.price < 0) {
      showToast('Price cannot be negative', 'error');
      return false;
    }

    if (formData.discount < 0) {
      showToast('Discount cannot be negative', 'error');
      return false;
    }

    if (formData.discount > formData.price) {
      showToast('Discount cannot exceed price', 'error');
      return false;
    }

    return true;
  }, [formData]);

  // Add new item to invoice
  const handleSubmitNewItem = async (e) => {
    if (e) e.preventDefault();
    
    if (!validateNewItem()) return;

    setIsSubmitting(true);
    
    try {
      const itemData = {
        description: formData.description.trim(),
        quantity: formData.quantity,
        price: formData.price,
        discount: formData.discount,
        tax_rate: formData.tax_rate,
        amount: formData.amount
      };

      onAddItem(itemData);
      showToast('Item added successfully', 'success');
      onHide();
    } catch (error) {
      console.error('Error adding new item:', error);
      showToast('Failed to add item. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Currency formatting
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }, [currency]);

  // Calculate total selected amount
  const getTotalSelectedAmount = useCallback(() => {
    return selectedProducts.reduce((total, product) => {
      return total + (product.selectedAmount || 0);
    }, 0);
  }, [selectedProducts]);

  // Calculate discounted price
  const calculateDiscountedPrice = useCallback((price, discount) => {
    return Math.max(0, (parseFloat(price) || 0) - (discount || 0));
  }, []);

  // Handle input changes for new item form
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => {
      let processedValue = value;
      
      // Validate and process numeric values
      if (['quantity', 'price', 'discount', 'tax_rate'].includes(field)) {
        processedValue = parseFloat(value) || 0;
        if (field === 'quantity') processedValue = Math.max(1, Math.floor(processedValue));
        if (['price', 'discount', 'tax_rate'].includes(field)) processedValue = Math.max(0, processedValue);
      }
      
      return {
        ...prev,
        [field]: processedValue
      };
    });
  }, []);

  if (!show) return null;

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-2xl shadow-lg w-full max-w-4xl p-6 max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-600">Loading products...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-4xl p-6 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center border-b pb-3 mb-4">
          <h5 className="text-lg font-semibold ">Add Items</h5>
          <button
            type="button"
            onClick={onHide}
            disabled={isSubmitting}
            className="text-gray-500 hover: text-xl disabled:opacity-50"
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b mb-4">
          <button
            type="button"
            className={`flex-1 py-2 px-4 text-center font-medium ${
              activeTab === 'existing'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('existing')}
            disabled={isSubmitting}
          >
            Select Existing Products
          </button>
          <button
            type="button"
            className={`flex-1 py-2 px-4 text-center font-medium ${
              activeTab === 'new'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('new')}
            disabled={isSubmitting}
          >
            Create New Item
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          {activeTab === 'existing' ? (
            <div className="flex-1 overflow-hidden flex">
              {/* Products List */}
              <div className="flex-1 overflow-hidden flex flex-col pr-4 border-r">
                {/* Search */}
                <div className="mb-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none pl-10"
                      disabled={isSubmitting}
                    />
                    <svg
                      className="w-5 h-5 text-gray-400 absolute left-3 top-2.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                {/* Products List */}
                <div className="flex-1 overflow-y-auto border rounded-lg divide-y">
                  {filteredProducts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p>No products found</p>
                      <p className="text-sm">Try a different search term</p>
                    </div>
                  ) : (
                    filteredProducts.map((product) => (
                      <div
                        key={product.id}
                        className={`p-4 cursor-pointer transition-colors ${
                          isProductSelected(product.id)
                            ? 'bg-blue-50 border-l-4 border-blue-600'
                            : 'hover:bg-gray-100'
                        }`}
                        onClick={() => !isSubmitting && handleProductSelect(product)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                                isProductSelected(product.id)
                                  ? 'bg-blue-600 border-blue-600'
                                  : 'border-gray-300'
                              }`}>
                                {isProductSelected(product.id) && (
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              <h4 className="font-medium text-gray-900">{product.product_name || 'Unnamed Product'}</h4>
                            </div>
                            <p className="text-sm text-gray-600 mt-1 ml-8">{product.description || 'No description'}</p>
                            {product.category && (
                              <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full mt-2 ml-8">
                                {product.category}
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-gray-900">
                              {formatCurrency(parseFloat(product.per_unit_cost) || 0)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Selected Products Panel */}
              <div className="w-1/3 pl-4">
                <h3 className="font-medium text-gray-700 mb-3">Selected Items ({selectedProducts.length})</h3>
                
                {selectedProducts.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 border rounded-lg">
                    <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                    <p>No items selected</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {selectedProducts.map((product) => {
                      const price = parseFloat(product.per_unit_cost) || 0;
                      const hasDiscount = product.selectedDiscount > 0;
                      const discountedPrice = calculateDiscountedPrice(price, product.selectedDiscount);
                      
                      return (
                        <div key={product.id} className="border rounded-lg p-3 bg-white">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium text-sm text-gray-900">{product.product_name || 'Unnamed Product'}</h4>
                            <button
                              type="button"
                              onClick={() => !isSubmitting && handleProductSelect(product)}
                              className="text-red-500 hover:text-red-700 disabled:opacity-50"
                              disabled={isSubmitting}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                          
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-xs text-gray-600">Quantity:</label>
                            <input
                              type="number"
                              min="1"
                              value={product.selectedQuantity}
                              onChange={(e) => updateSelectedProductQuantity(product.id, parseInt(e.target.value) || 1)}
                              className="w-16 px-2 py-1 border rounded text-sm text-center"
                              disabled={isSubmitting}
                            />
                          </div>
                          
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-xs text-gray-600">Discount ({currency}):</label>
                            <input
                              type="number"
                              min="0"
                              max={price}
                              step="0.01"
                              value={product.selectedDiscount}
                              onChange={(e) => updateSelectedProductDiscount(product.id, parseFloat(e.target.value) || 0)}
                              className="w-20 px-2 py-1 border rounded text-sm text-center"
                              disabled={isSubmitting}
                            />
                          </div>
                          
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-xs text-gray-500">Price:</span>
                            <div className="text-right">
                              {hasDiscount ? (
                                <>
                                  <div className="text-xs text-gray-400 line-through">
                                    {formatCurrency(price)}
                                  </div>
                                  <div className="text-sm font-semibold text-green-600">
                                    {formatCurrency(discountedPrice)}
                                  </div>
                                  <div className="text-xs text-red-500">
                                    -{formatCurrency(product.selectedDiscount)}
                                  </div>
                                </>
                              ) : (
                                <span className="text-sm font-semibold">
                                  {formatCurrency(price)}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex justify-between items-center mt-2 pt-2 border-t">
                            <span className="text-xs text-gray-500">Subtotal:</span>
                            <span className="text-sm font-semibold">
                              {formatCurrency(product.selectedAmount)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Total Summary */}
                {selectedProducts.length > 0 && (
                  <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                    <div className="flex justify-between items-center font-semibold">
                      <span>Total:</span>
                      <span>{formatCurrency(getTotalSelectedAmount())}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              <form onSubmit={handleSubmitNewItem} className="space-y-4">
                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    required
                    placeholder="Enter item description"
                    className="w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Quantity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantity *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.quantity}
                      onChange={(e) => handleInputChange('quantity', e.target.value)}
                      required
                      className="w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price ({currency}) *
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', e.target.value)}
                      required
                      className="w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Discount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Discount ({currency})
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      max={formData.price}
                      value={formData.discount}
                      onChange={(e) => handleInputChange('discount', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Tax Rate */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tax Rate (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.tax_rate}
                      onChange={(e) => handleInputChange('tax_rate', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                {/* Amount (read-only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Amount
                  </label>
                  <div className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-600 shadow-sm cursor-not-allowed font-semibold">
                    {formData.discount > 0 ? (
                      <div className="text-right">
                        <div className="text-xs text-gray-400 line-through">
                          {formatCurrency(formData.quantity * formData.price)}
                        </div>
                        <div className="text-green-600">
                          {formatCurrency(formData.amount)}
                        </div>
                        <div className="text-xs text-red-500">
                          Discount: -{formatCurrency(formData.discount * formData.quantity)}
                        </div>
                      </div>
                    ) : (
                      formatCurrency(formData.amount)
                    )}
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Footer buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t mt-4">
            <button
              type="button"
              onClick={onHide}
              disabled={isSubmitting}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            
            {activeTab === 'existing' ? (
              <button
                type="button"
                onClick={handleAddSelectedProducts}
                disabled={selectedProducts.length === 0 || isSubmitting}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Adding...
                  </>
                ) : (
                  `Add ${selectedProducts.length} Item${selectedProducts.length !== 1 ? 's' : ''}`
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmitNewItem}
                disabled={!formData.description.trim() || formData.quantity <= 0 || formData.price < 0 || formData.discount < 0 || formData.discount > formData.price || isSubmitting}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Adding...
                  </>
                ) : (
                  'Add New Item'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddItemModal;